import { useEffect, type FC } from "react";

import { Box, useTheme, Theme } from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { makeStyles } from "@mui/styles";
import CampaignNotifications from "./CampaignNotifications";
import CampaignView from "./CampaignView";
import CustomButton from "../../../components/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import CampaignAnalytics from "./CampaignAnalytics";
import { getAdminInfo } from "../../../services/selectors/user-selectors";
import { useTranslation } from "react-i18next";
import HelpButton from "../../../components/HelpButton";
import { CampaignSettings } from "../CampaignSettings/CampaignSettings";
import { AddCampaignTitle } from "./AddCampaignTitle";
import { Branding } from "../CampaignBranding/Branding";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useEditCampaign } from "../../../context/EditCampaignContext";
import { showFlashMessageWithTimeout } from "../../../services/actions/flashMessage-actions";
import { useCampaign } from "../../../context/CampaignContext";
import { getCustomWebLogo } from "../../../services/selectors/web-selectors";

export const AddCampaign: FC = () => {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const admin = useSelector(getAdminInfo);
  const customWebBranding = useSelector(getCustomWebLogo);

  const {
    addEmptyForm,
    alignment,
    campaignName,
    createPcPreview,
    createPhonePreview,
    editCampaignName,
    forms,
    getLocalSettings,
    getValuesToHide,
    handleAlignmentChange,
    handleChange,
    handleFormControlChange,
    handleEditCampaignName,
    isDefaultUpdateLoading,
    isFetching,
    isLoading,
    isModalOpen,
    menuItems,
    onAddExcludeChip,
    onAddFormIncludeChip,
    onCampaignNameSave,
    onDeleteForm,
    onSaveChanges,
    pcPreview,
    phonePreview,
    selectedBorder,
    selectedTab,
    setIsDefaultUpdateLoading,
    setIsModalOpen,
    setPcPreview,
    setPhonePreview,
    pcSettings,
    phoneSettings,
    setPcSettings,
    setPhoneSettings,
    activeSize,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    modalOpen,
    setActiveSize,
    setBackgroundColor,
    setBackgroundOpacity,
    setBorderRadius,
    setIconBackgroundColor,
    setIconBackgroundRadius,
    setIconColor,
    setModalOpen,
    setShowIcon,
    setTextColor,
    showIcon,
    textColor,
  } = useCampaign();

  const { detectedChange, save, setReset, setSave } = useEditCampaign();

  const activeColor = theme.palette.natural.t1;
  const notActiveColor = theme.palette.natural.t2;

  useEffect(() => {
    if (save) {
      showFlashMessageWithTimeout(
        dispatch,
        t("popups.saveWasSuccessful"),
        "success",
        2000,
      );
    }
  }, [save]);

  return (
    <Box className={classes.container}>
      <HelpButton link='https://www.overenyweb.cz/napoveda/zacnete-zobrazovat-notifikace/spustte-prvni-kampan/' />
      <AddCampaignTitle
        isFetching={isFetching}
        campaignName={campaignName}
        edit={editCampaignName}
        onEdit={handleEditCampaignName}
        onSave={onCampaignNameSave}
      />
      <Box sx={{ width: "100%" }}>
        <TabContext value={selectedTab}>
          <TabList
            TabIndicatorProps={{
              style: {
                background: theme.palette.main.primary.o,
                height: 3,
              },
            }}
            onChange={handleChange}
          >
            <Tab
              disableRipple
              className={classes.tab}
              style={{
                color: selectedTab === "view" ? activeColor : notActiveColor,
              }}
              label={t("campaign.design.title")}
              value='view'
            />
            <Tab
              disableRipple
              className={classes.tab}
              style={{
                color:
                  selectedTab === "notifications"
                    ? activeColor
                    : notActiveColor,
              }}
              label={t("campaign.notifications.title")}
              value='notifications'
            />
            <Tab
              disableRipple
              className={classes.tab}
              style={{
                color:
                  selectedTab === "branding" ? activeColor : notActiveColor,
              }}
              label={t("campaign.branding.title")}
              value='branding'
            />
            <Tab
              disableRipple
              className={classes.tab}
              style={{
                color:
                  selectedTab === "settings" ? activeColor : notActiveColor,
              }}
              label={t("campaign.settings.title")}
              value='settings'
            />
            {admin.isAdmin && (
              <Tab
                disableRipple
                className={classes.tab}
                style={{
                  color:
                    selectedTab === "analytics" ? activeColor : notActiveColor,
                }}
                label={t("campaign.analytics.title")}
                value='analytics'
              />
            )}
            <Box style={{ flex: 1 }} />
            <Box className={classes.tabsDivider} />
            {detectedChange && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 3,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: theme.palette.natural.t3,
                  borderBottomWidth: 3,
                  borderBottomStyle: "solid",
                  position: "relative",
                  zIndex: theme.zIndex.appBar,
                }}
              >
                <RestartAltIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setReset(true)}
                />
                <CustomButton
                  size='small'
                  type='primary'
                  title={t("saveChanges")}
                  style={{
                    padding: "0 8px",
                  }}
                  onClick={() => setSave(true)}
                />
              </Box>
            )}
          </TabList>
          <TabPanel value='settings' style={{ padding: 0 }}>
            <CampaignSettings
              addEmptyPairingForm={addEmptyForm}
              alignment={alignment}
              forms={forms}
              getValuesToHide={getValuesToHide}
              handleAlignmentChange={handleAlignmentChange}
              handleFormControlChange={handleFormControlChange}
              isFetching={isFetching}
              isLoading={isLoading}
              menuItems={menuItems}
              onAddExcludeChip={onAddExcludeChip}
              onAddFormIncludeChip={onAddFormIncludeChip}
              onDeleteForm={onDeleteForm}
              onSaveChanges={onSaveChanges}
            />
          </TabPanel>
          <TabPanel value='branding' style={{ padding: 0 }}>
            <Branding
              activeSize={activeSize}
              backgroundColor={backgroundColor}
              backgroundOpacity={backgroundOpacity}
              borderRadius={borderRadius}
              iconBackgroundColor={iconBackgroundColor}
              iconBackgroundRadius={iconBackgroundRadius}
              iconColor={iconColor}
              modalOpen={modalOpen}
              setActiveSize={setActiveSize}
              setBackgroundColor={setBackgroundColor}
              setBackgroundOpacity={setBackgroundOpacity}
              setBorderRadius={setBorderRadius}
              setIconBackgroundColor={setIconBackgroundColor}
              setIconBackgroundRadius={setIconBackgroundRadius}
              setIconColor={setIconColor}
              setModalOpen={setModalOpen}
              setShowIcon={setShowIcon}
              setTextColor={setTextColor}
              showIcon={showIcon}
              textColor={textColor}
            />
          </TabPanel>
          <TabPanel value='view' style={{ padding: 0 }}>
            <CampaignView
              getLocalSettings={getLocalSettings}
              isDefaultUpdateLoading={isDefaultUpdateLoading}
              isModalOpen={isModalOpen}
              isLoading={isLoading}
              isFetching={isFetching}
              pcPreview={pcPreview}
              phonePreview={phonePreview}
              selectedBorder={selectedBorder}
              setIsDefaultUpdateLoading={setIsDefaultUpdateLoading}
              setIsModalOpen={setIsModalOpen}
              setPcPreview={setPcPreview}
              setPhonePreview={setPhonePreview}
              createPcPreview={createPcPreview}
              createPhonePreview={createPhonePreview}
              pcSettings={pcSettings}
              phoneSettings={phoneSettings}
              setPcSettings={setPcSettings}
              setPhoneSettings={setPhoneSettings}
            />
          </TabPanel>
          <TabPanel value='notifications' style={{ padding: 0 }}>
            <CampaignNotifications
              branding={{
                brandingIsShowingIcon: showIcon,
                activeSize,
                backgroundColor,
                backgroundOpacity,
                borderRadius,
                iconBackgroundColor,
                iconBackgroundRadius,
                iconColor,
                textColor,
                logoDisplay: customWebBranding?.logoDisplay,
                logo: customWebBranding?.logo,
                logoBackgroundColor: customWebBranding?.logoBackgroundColor,
                logoText: customWebBranding?.logoText,
              }}
            />
          </TabPanel>
          <TabPanel value='analytics' style={{ padding: 0 }}>
            <CampaignAnalytics />
          </TabPanel>
        </TabContext>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    marginLeft: 24,
    marginRight: 24,
    marginTop: 24,
  },
  tabsDivider: {
    width: "100%",
    height: 3,
    backgroundColor: theme.palette.natural.t3,
    position: "absolute",
    bottom: 0,
  },
  tab: {
    textTransform: "none",
    fontSize: 16,
  },
}));
